import { useState, useEffect, useMemo, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight, Download, FileText, Loader2, Printer, ZoomIn, ZoomOut } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface PdfViewerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  fileUrl: string;
  pdfUrl?: string | null;
  policyId?: string;
  title?: string;
}

function getFileExtension(url: string): string {
  const clean = url.split('?')[0].split('#')[0];
  return (clean.split('.').pop() ?? '').toLowerCase();
}

function getStoragePath(fileUrl: string): string | null {
  if (!fileUrl) return null;
  try {
    const url = new URL(fileUrl);
    const match = url.pathname.match(/\/storage\/v1\/object(?:\/public)?\/([^/]+)\/(.+)/);
    if (match) return match[2];
    return null;
  } catch {
    if (fileUrl.includes('/')) return fileUrl;
    return null;
  }
}

export function PdfViewerDialog({ open, onOpenChange, fileUrl, pdfUrl, policyId, title }: PdfViewerDialogProps) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const blobUrlRef = useRef<string | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [composerHtml, setComposerHtml] = useState<string | null>(null);
  const [isLoadingHtml, setIsLoadingHtml] = useState(false);
  const { toast } = useToast();

  const hasNoFile = !fileUrl && !pdfUrl;
  const ext = hasNoFile ? '' : getFileExtension(fileUrl || '');
  // If a converted PDF is available, always show inline; otherwise fall back to extension check
  const hasPdfUrl = !!pdfUrl;
  const isWordDoc = !hasPdfUrl && (ext === 'doc' || ext === 'docx');
  // Use the converted PDF URL for inline viewing when available, otherwise use fileUrl
  const effectiveUrl = hasPdfUrl ? pdfUrl! : fileUrl;

  const documentOptions = useMemo(() => ({
    cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
  }), []);

  useEffect(() => {
    if (!open || !effectiveUrl) {
      // Revoke previous blob URL
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
      setBlobUrl(null);
      setPageNumber(1);
      setError(null);
      return;
    }

    if (isWordDoc) return;

    async function fetchPdf() {
      setIsLoading(true);
      setError(null);

      try {
        const storagePath = getStoragePath(effectiveUrl);
        if (!storagePath) throw new Error('Invalid file URL format');

        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) throw new Error('Not authenticated');

        const res = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/policy-document-download?path=${encodeURIComponent(storagePath)}`,
          { headers: { Authorization: `Bearer ${session.access_token}` } }
        );

        if (!res.ok) {
          const text = await res.text().catch(() => '');
          throw new Error(text || `Download failed (${res.status})`);
        }

        const arrayBuffer = await res.arrayBuffer();
        const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);

        // Revoke previous blob URL before setting new one
        if (blobUrlRef.current) {
          URL.revokeObjectURL(blobUrlRef.current);
        }
        blobUrlRef.current = url;
        setBlobUrl(url);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load PDF';
        setError(message);
        toast({
          title: 'Failed to load document',
          description: message,
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    }

    fetchPdf();

    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [open, effectiveUrl, toast, isWordDoc]);

  // Fetch Composer HTML content when no file is present
  useEffect(() => {
    if (!open || !hasNoFile || !policyId) {
      setComposerHtml(null);
      return;
    }

    async function fetchComposerHtml() {
      setIsLoadingHtml(true);
      try {
        const { data, error: fetchError } = await supabase
          .from('policy_drafts')
          .select('content_html')
          .eq('policy_id', policyId!)
          .eq('status', 'submitted')
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        if (fetchError) throw fetchError;
        setComposerHtml(data?.content_html || null);
      } catch (err) {
        console.error('[PdfViewer] Failed to fetch composer HTML:', err);
        setComposerHtml(null);
      } finally {
        setIsLoadingHtml(false);
      }
    }

    fetchComposerHtml();
  }, [open, hasNoFile, policyId]);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
  }

  function goToPrevPage() { setPageNumber((prev) => Math.max(prev - 1, 1)); }
  function goToNextPage() { setPageNumber((prev) => Math.min(prev + 1, numPages)); }
  function zoomIn() { setScale((prev) => Math.min(prev + 0.25, 3)); }
  function zoomOut() { setScale((prev) => Math.max(prev - 0.25, 0.5)); }

  async function handleDownload() {
    if (!blobUrl) {
      // Word doc or data not yet loaded — fetch on demand
      setIsDownloading(true);
      try {
        const storagePath = getStoragePath(fileUrl);
        if (!storagePath) throw new Error('Invalid file URL format');

        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) throw new Error('Not authenticated');

        const res = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/policy-document-download?path=${encodeURIComponent(storagePath)}`,
          { headers: { Authorization: `Bearer ${session.access_token}` } }
        );

        if (!res.ok) {
          const text = await res.text().catch(() => '');
          throw new Error(text || `Download failed (${res.status})`);
        }

        const arrayBuffer = await res.arrayBuffer();
        const mimeType = isWordDoc
          ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
          : 'application/pdf';
        const blob = new Blob([arrayBuffer], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = title ? `${title}.${ext}` : `document.${ext}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (err) {
        toast({
          title: 'Download failed',
          description: err instanceof Error ? err.message : 'Unknown error',
          variant: 'destructive',
        });
      } finally {
        setIsDownloading(false);
      }
      return;
    }

    // PDF already loaded — use existing blob URL
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = title ? `${title}.pdf` : 'document.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  function handlePrint() {
    if (!blobUrl) return;
    try { window.print(); } catch (e) { console.error('Print failed:', e); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[90vh] flex flex-col p-0">
        <style>{`
          @media print {
            body * { visibility: hidden !important; }
            #pdf-print-root, #pdf-print-root * { visibility: visible !important; }
            #pdf-print-root {
              position: absolute !important; left: 0 !important; top: 0 !important;
              width: 100% !important; height: auto !important; overflow: visible !important;
            }
          }
        `}</style>
        <DialogHeader className="px-6 py-4 border-b flex-shrink-0">
          <DialogTitle className="flex items-center justify-between pr-8">
            <span className="truncate">{title || 'Document Viewer'}</span>
          </DialogTitle>
        </DialogHeader>

        {hasNoFile ? (
          isLoadingHtml ? (
            <div className="flex-1 flex items-center justify-center gap-2 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" /> Loading document...
            </div>
          ) : composerHtml ? (
            <>
              <div className="flex items-center justify-between px-4 py-2 border-b bg-muted/50 flex-shrink-0">
                <Badge variant="outline" className="gap-1 border-[hsl(180,50%,40%)] bg-[hsl(180,50%,95%)] text-[hsl(180,50%,30%)]">
                  <FileText className="h-3 w-3" />
                  Created in Policy Builder
                </Badge>
                <Button variant="outline" size="sm" onClick={() => window.print()}>
                  <Printer className="h-4 w-4 mr-1" /> Print
                </Button>
              </div>
              <div id="pdf-print-root" className="flex-1 overflow-auto bg-white dark:bg-white">
                <div
                  className="px-12 py-10 max-w-none prose prose-sm text-black"
                  dangerouslySetInnerHTML={{ __html: composerHtml }}
                />
              </div>
            </>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
              <p className="text-lg font-medium text-foreground">Document preview unavailable</p>
              <p className="text-sm text-muted-foreground mt-2">This policy was created in Policy Builder.</p>
            </div>
          )
        ) : isWordDoc ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <p className="text-lg font-medium text-foreground">This document was uploaded as a Word file.</p>
            <p className="text-sm text-muted-foreground mt-2">Word documents cannot be previewed inline.</p>
            <Button variant="default" className="mt-6" onClick={handleDownload} disabled={isDownloading}>
              {isDownloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Download Document
            </Button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between px-4 py-2 border-b bg-muted/50 flex-shrink-0">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={goToPrevPage} disabled={pageNumber <= 1}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground min-w-[80px] text-center">
                  {numPages > 0 ? `${pageNumber} / ${numPages}` : '—'}
                </span>
                <Button variant="outline" size="sm" onClick={goToNextPage} disabled={pageNumber >= numPages}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={zoomOut} disabled={scale <= 0.5}>
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground min-w-[50px] text-center">
                  {Math.round(scale * 100)}%
                </span>
                <Button variant="outline" size="sm" onClick={zoomIn} disabled={scale >= 3}>
                  <ZoomIn className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={handlePrint} disabled={!blobUrl}>
                  <Printer className="h-4 w-4 mr-1" /> Print
                </Button>
                <Button variant="outline" size="sm" onClick={handleDownload} disabled={!blobUrl}>
                  <Download className="h-4 w-4 mr-1" /> Download
                </Button>
              </div>
            </div>

            <div id="pdf-print-root" className="flex-1 overflow-auto flex items-start justify-center p-4 bg-muted/30">
              {isLoading && (
                <div className="flex items-center gap-2 text-muted-foreground py-12">
                  <Loader2 className="h-5 w-5 animate-spin" /> Loading document...
                </div>
              )}

              {error && !isLoading && (
                <div className="text-destructive py-12 text-center">
                  <p className="font-medium">Unable to load document</p>
                  <p className="text-sm text-muted-foreground mt-1">{error}</p>
                </div>
              )}

              {blobUrl && !isLoading && !error && (
                <Document
                  file={blobUrl}
                  onLoadSuccess={onDocumentLoadSuccess}
                  onLoadError={(err) => {
                    console.error('[PdfViewer] Document load error:', err);
                    setError(err?.message || 'Failed to render PDF');
                  }}
                  options={documentOptions}
                  loading={
                    <div className="flex items-center gap-2 text-muted-foreground py-12">
                      <Loader2 className="h-5 w-5 animate-spin" /> Rendering...
                    </div>
                  }
                  error={
                    <div className="text-destructive py-12 text-center">
                      <p className="font-medium">Failed to render PDF</p>
                      <p className="text-sm text-muted-foreground mt-1">The document could not be displayed inline.</p>
                      <Button variant="default" className="mt-4" onClick={handleDownload}>
                        <Download className="h-4 w-4 mr-2" /> Download PDF instead
                      </Button>
                    </div>
                  }
                >
                  <Page pageNumber={pageNumber} scale={scale} renderTextLayer={true} renderAnnotationLayer={true} />
                </Document>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
